// User-friendly mappings for Firebase Auth error codes encountered in
// OTP send / verify flows on web (#bug-pair-2). Shared by Signup,
// ForgotPassword, AddPhonePrompt. Mirror in apps/mobile/src/services/
// otpService.ts for mobile parity.
//
// Generic catch-all 'Failed to send OTP' / 'Failed to verify OTP'
// remains as fallback. Mapped codes return a more actionable message
// so the user knows whether to fix the number, wait, switch network,
// or sign in with an existing account.

export interface MappedFirebaseError {
  /** Stable bucket for analytics + downstream branching. */
  kind:
    | 'phone-already-registered'
    | 'invalid-phone'
    | 'too-many-requests'
    | 'network'
    | 'verifier-misconfigured'
    | 'invalid-otp'
    | 'otp-expired'
    | 'unknown'
  /** User-facing copy. */
  message: string
}

/** Try to extract a Firebase auth code from anything: code string,
 *  Error.message ("Firebase: Error (auth/foo)."), axios payload, etc. */
function extractCode(input: unknown): string {
  if (!input) return ''
  if (typeof input === 'string') return input
  const err = input as { code?: string; message?: string }
  if (err.code) return err.code
  if (err.message) {
    const match = err.message.match(/auth\/[a-z0-9-]+/)
    if (match) return match[0]
  }
  return ''
}

export function mapFirebaseAuthSendOtpError(input: unknown): MappedFirebaseError {
  const code = extractCode(input)
  switch (code) {
    case 'auth/phone-number-already-exists':
    case 'auth/credential-already-in-use':
      return {
        kind: 'phone-already-registered',
        message: 'This number is already on Jnati. Sign in with it instead.',
      }
    case 'auth/invalid-phone-number':
      return {
        kind: 'invalid-phone',
        message: 'That phone number doesn\'t look right. Please check and try again.',
      }
    case 'auth/too-many-requests':
      return {
        kind: 'too-many-requests',
        message: 'Too many attempts. Please wait a few minutes and try again.',
      }
    case 'auth/network-request-failed':
      return {
        kind: 'network',
        message: 'Network error. Check your connection and try again.',
      }
    case 'auth/argument-error':
    case 'auth/internal-error':
      return {
        kind: 'verifier-misconfigured',
        message: 'Verification could not start. Please reload the page and try again.',
      }
    default:
      return {
        kind: 'unknown',
        message: 'Failed to send OTP. Check the number and try again.',
      }
  }
}

export function mapFirebaseAuthVerifyOtpError(input: unknown): MappedFirebaseError {
  const code = extractCode(input)
  switch (code) {
    case 'auth/invalid-verification-code':
      return { kind: 'invalid-otp', message: 'Invalid OTP. Please try again.' }
    case 'auth/code-expired':
      return { kind: 'otp-expired', message: 'OTP expired. Tap "Resend OTP" to get a new one.' }
    case 'auth/too-many-requests':
      return { kind: 'too-many-requests', message: 'Too many attempts. Please wait a few minutes and try again.' }
    case 'auth/network-request-failed':
      return { kind: 'network', message: 'Network error. Check your connection and try again.' }
    default:
      return { kind: 'unknown', message: 'Failed to verify OTP. Please try again.' }
  }
}
